/**
 * What a vault looks like, as a thing you can turn.
 *
 * The layout itself is a force simulation now rather than arithmetic here --
 * bodies that repel, links that pull like rubber bands, and a centre that stops
 * the whole thing drifting apart. Those are the same three forces Obsidian
 * exposes in its graph view, for the same reason: a vault has no natural
 * coordinates, so the only honest arrangement is the one its own links settle
 * into.
 *
 * What stays here is everything the simulation does not decide -- what a thing
 * is called, what colour it is, how big it should be, and what is joined to
 * what. Pure, so it can be tested without a WebGL context.
 */

export interface DocNode {
  name: string;
  kind: 'entity' | 'episode' | 'document';
  source: string;
  description: string;
  /** How many other notes point here. */
  degree: number;
  /** The course this belongs to, where it belongs to one. */
  cluster: string | null;
}

export interface DocEdge {
  from: string;
  to: string;
}

/** The page everything else is written into. */
export const CENTRE = 'user';

/**
 * What everything looks like until you touch it.
 *
 * A vault at rest is three and a half thousand notes, and colouring all of them
 * at once is a fruit salad that tells you nothing -- every dot shouting its
 * category while you are trying to see a shape. So the resting state is one
 * quiet colour, bright enough to see, and colour is what selection *does*.
 */
export const RESTING = 'rgba(168,178,212,0.85)';

/** The pages. */
const PAGE_COLOURS: Record<string, string> = {
  user: '#f0abfc',
  chats: '#34d399',
  school: '#fbbf24',
};
const CLASS = '#a78bfa';

/*
 * The notes, by what they are rather than by which page they hang from.
 *
 * Brighter than the app's own palette, because these sit on near-black: a
 * small point needs a ground to be bright against.
 */
const NOTE_COLOURS: Record<string, string> = {
  Course: '#c4b5fd',
  Assignment: '#60a5fa',
  Topic: '#22d3ee',
  Person: '#f0abfc',
  Material: '#fbbf24',
};
const OWN_FILE = '#a3e635';
const GIVEN_FILE = '#fb923c';
const SAID = '#34d399';

/**
 * What a person's page is named for: the note it was written from.
 *
 * A vault has ten pages and one of these per person it knows, which is
 * hundreds. So a person's page is a note in this picture, whatever the vault
 * files it as -- drawn as pages they were hundreds of thirteen-unit purple
 * spheres, indistinguishable from the classes, inflating the ball they sit in
 * past the point where any of it could be read.
 */
const PERSON_PAGE = /^person-/;

/** Somebody: the note about them, or the page written from that note. */
function isPerson(node: DocNode): boolean {
  return node.description === 'Person' || PERSON_PAGE.test(node.name);
}

/** A page proper -- one of the ten a student navigates by, not a person. */
function isPage(node: DocNode): boolean {
  return node.kind === 'document' && !isPerson(node);
}

/** The colour a thing has when it is what you are looking at, or next to it. */
function ownColour(node: DocNode): string {
  if (isPerson(node)) return NOTE_COLOURS.Person as string;
  if (node.kind === 'document') return PAGE_COLOURS[node.name] ?? CLASS;
  if (node.kind === 'episode') {
    // What the student said themselves is the one thing here they wrote.
    if (node.source === 'student') return SAID;
    return node.source === 'classroom' ? '#94a3b8' : '#7c8bb0';
  }
  if (node.description === 'File') return node.source === 'drive' ? OWN_FILE : GIVEN_FILE;
  return NOTE_COLOURS[node.description] ?? '#8a8fae';
}

/**
 * What to draw a node in, given what is held.
 *
 * Nothing held and everything is at rest, which is the state a vault spends
 * most of its time in. Hold something and it and everything it touches say what
 * they are; the rest stay exactly as visible as they were, because dimming
 * three thousand notes to make one legible leaves a picture of one note.
 */
export function colourFor(node: DocNode, lit: Lit): string {
  return isLit(node, lit) ? ownColour(node) : RESTING;
}

/** What the colours mean. Without it, a picture nobody can read. */
export const KEY: { colour: string; label: string }[] = [
  { colour: PAGE_COLOURS.user as string, label: 'You' },
  { colour: CLASS, label: 'Your classes' },
  { colour: PAGE_COLOURS.school as string, label: 'Your school' },
  { colour: PAGE_COLOURS.chats as string, label: 'What you have told it' },
  { colour: NOTE_COLOURS.Course as string, label: 'Courses' },
  { colour: NOTE_COLOURS.Assignment as string, label: 'Work' },
  { colour: NOTE_COLOURS.Material as string, label: 'Readings' },
  { colour: GIVEN_FILE, label: 'Files from class' },
  { colour: OWN_FILE, label: 'Your own files' },
  { colour: '#94a3b8', label: 'Things that happened' },
];

/**
 * How big a thing is drawn.
 *
 * The pages are what somebody is navigating by and there are ten of them; the
 * notes are what they were written from and there are thousands. Sizing them
 * alike would bury the ten. Beyond that, a note more of the vault points at is
 * drawn larger, which is the one thing the graph knows about importance.
 */
export function radiusFor(node: DocNode): number {
  if (node.name === CENTRE) return 22;
  if (isPage(node)) return 13;
  if (node.description === 'Course') return 7;
  return Math.min(5, 2.6 + node.degree * 0.3);
}

/**
 * The multiplier the renderer applies after taking a cube root.
 *
 * Its formula, from its own source, is `cbrt(val) * nodeRelSize`. That is the
 * only reason this constant exists: left at its default of four, every sphere
 * came out four times the radius the forces were keeping apart, and the school
 * settled a long way inside the student while collision reported itself
 * satisfied. One, so a value in equals a radius out.
 */
export const NODE_REL_SIZE = 1;

/**
 * What the renderer wants, which is a volume rather than a radius.
 *
 * The inverse of its own sizing, so that what it draws is exactly what the
 * forces are holding apart. Everything about the arrangement depends on those
 * two agreeing, and they are two different pieces of arithmetic in two
 * different libraries -- so the conversion lives here, with a test that runs
 * the renderer's formula over it.
 */
export function volumeFor(node: DocNode): number {
  return (radiusFor(node) / NODE_REL_SIZE) ** 3;
}

/** What the renderer will actually draw, by its own formula. For tests to check. */
export function drawnRadius(node: DocNode): number {
  return Math.cbrt(volumeFor(node)) * NODE_REL_SIZE;
}

/**
 * How tall a name is drawn, in the same units the simulation works in.
 *
 * Sized against the link distance rather than the node: a name has to be
 * readable from far enough away to be worth having, and a label scaled to a
 * three-unit dot is a smudge. The first version used eight units on a graph a
 * thousand across, which is how the names came out invisible.
 */
export function labelHeight(node: DocNode): number {
  if (node.name === CENTRE) return 26;
  if (isPage(node)) return 18;
  if (node.description === 'Course') return 12;
  return 8;
}

/**
 * "class-french" is the filename; "French" is what a student calls it.
 *
 * The person prefix goes the same way. A page has to be called something other
 * than the note it was written from or a wikilink would reach either, but that
 * is a filing problem, and "person mme rivard" is not anybody's name.
 */
export function labelFor(name: string): string {
  return name.replace(/^(?:class|person)-/, '').replaceAll('-', ' ');
}

/**
 * How many names to show.
 *
 * Every name is a texture on the graphics card, and three and a half thousand
 * of them is both seconds of building and, at any distance where the whole
 * graph fits on screen, a wall of overlapping text. This is about what fits
 * before the names start colliding with each other.
 */
export const NAMES_SHOWN = 120;

/**
 * Which names are worth showing, decided once.
 *
 * The landmarks always -- the pages and the courses -- because a graph whose
 * landmarks are unlabelled is a field of dots. Then the notes the rest of the
 * vault points at most, which is the only thing the graph itself knows about
 * importance.
 *
 * Fixed rather than chosen by where the camera is. Names that appear and
 * vanish as you drift make a picture that will not hold still, and the whole
 * point of a map is that you can learn where things are on it.
 */
export function importantNames(nodes: readonly DocNode[], most = NAMES_SHOWN): Set<string> {
  const names = new Set<string>();
  const rest: DocNode[] = [];

  for (const node of nodes) {
    if (isPage(node) || node.description === 'Course') names.add(node.name);
    else rest.push(node);
  }

  for (const node of [...rest].sort((a, b) => b.degree - a.degree)) {
    if (names.size >= most) break;
    if (node.degree === 0) break;
    names.add(node.name);
  }

  return names;
}

/** Whether this one says what it is: because it matters, you are on it, or you looked for it. */
export function labelled(
  node: DocNode,
  names: ReadonlySet<string>,
  held: string | null,
  found: ReadonlySet<string> = NONE,
): boolean {
  return names.has(node.name) || node.name === held || found.has(node.name);
}

const NONE: ReadonlySet<string> = new Set();

/**
 * Which notes a search lights up.
 *
 * Decided from what the picture already knows about every note -- its name
 * and the one line describing it -- so it answers as you type, with nothing
 * fetched. For a note from mail or Classroom that line is a sentence naming
 * the teacher and the work, which is most of what anybody looks for.
 *
 * Every word typed has to be there. One of them matching is a search for
 * "cold war" that lights up every note with "war" in it.
 */
export function find(nodes: readonly DocNode[], query: string): Set<string> {
  const words = terms(query);
  if (words.length === 0) return new Set();

  const found = new Set<string>();
  for (const note of nodes) {
    const text = plain(`${labelFor(note.name)} ${note.description}`);
    if (words.every((word) => text.includes(word))) found.add(note.name);
  }
  return found;
}

/** Lowercase, without accents: "Québec" and "quebec" are the same word to a search. */
function plain(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function terms(query: string): string[] {
  return plain(query)
    .split(/[^\p{L}\p{N}]+/u)
    .filter((word) => word !== '');
}

/** Every note directly joined to this one, in either direction. */
export function neighbours(edges: readonly DocEdge[], name: string): Set<string> {
  const found = new Set<string>();
  for (const edge of edges) {
    if (edge.from === name) found.add(edge.to);
    if (edge.to === name) found.add(edge.from);
  }
  return found;
}

/**
 * What is lit: the thing held and what it touches, and whatever a search found.
 *
 * Nothing held and nothing found means everything is at rest, which is what
 * stops a resting graph looking like it is switched off.
 */
export type Lit = { name: string | null; joined: Set<string>; found?: ReadonlySet<string> } | null;

export function litBy(
  edges: readonly DocEdge[],
  held: string | null,
  found: ReadonlySet<string> = NONE,
): Lit {
  if (!held && found.size === 0) return null;
  return { name: held, joined: held ? neighbours(edges, held) : new Set(), found };
}

export function isLit(node: DocNode, lit: Lit): boolean {
  if (!lit) return false;
  return lit.name === node.name || lit.joined.has(node.name) || lit.found?.has(node.name) === true;
}

/**
 * The gap left between two nodes that are touching, in world units.
 *
 * Not zero: spheres that meet exactly still read as one shape, and a link
 * between them has nowhere to be drawn.
 */
export const NODE_GAP = 7;

/**
 * How far apart two linked notes want to sit.
 *
 * Derived from what they are drawn as, which is the whole rule: a link between
 * two big pages has to be longer than a link between two small notes, or the
 * force pulling them together and the force keeping them apart are asking for
 * different things and the answer is a half-swallowed sphere.
 *
 * A fixed distance is what put the school inside the student.
 */
export function linkDistanceFor(a: DocNode, b: DocNode): number {
  return radiusFor(a) + radiusFor(b) + NODE_GAP;
}

/**
 * How much room a node claims for itself.
 *
 * Half the gap each, so two touching nodes claim exactly the distance their
 * link wants. The two forces then agree rather than fighting, which is the
 * difference between nodes that settle apart and nodes that settle overlapping
 * because one force is slightly stronger than the other.
 */
export function collideRadiusFor(node: DocNode): number {
  return radiusFor(node) + NODE_GAP / 2;
}

/**
 * The forces, tuned the way Obsidian tunes its own.
 *
 * Repel separates unrelated notes, link pulls related ones together like a
 * rubber band, the middle holds the whole thing together, and collide is the
 * one that is not a preference: nothing may sit inside anything else.
 */
export const FORCES = {
  /**
   * Node repulsion. Negative pushes apart.
   *
   * Gentler than the library's default, because repulsion compounds: three
   * thousand bodies all pushing produce a ball so large that every node in it
   * is a speck, however big the node is drawn. Gentler again now that the
   * middle pulls back, which is what decides how big the ball is -- this only
   * has to keep unrelated notes off each other.
   */
  charge: -18,
  /** How tightly a link pulls back to the length it wants, 0 to 1. */
  linkStrength: 0.4,
  /**
   * How hard the middle pulls everything home, 0 to 1.
   *
   * The force this file always claimed to have and never did. What the renderer
   * registers as its centre is a translation -- it moves the whole arrangement
   * so its average lands on the origin, and pulls on nothing. So the only thing
   * setting the size of the ball was repulsion against nothing at all, and a
   * ball that is only pushed outwards has no size it settles at: it expands for
   * as long as the simulation is allowed to run, and stops because the
   * simulation cooled rather than because it was finished.
   *
   * On a real vault that came out about four thousand units across against notes
   * three units wide, which is a picture where the whole thing framed on screen
   * draws every note at half a pixel. Eight hundred notes joined to nothing are
   * what makes it so: with nothing linking them inward, repulsion is the only
   * thing they feel.
   *
   * A pull towards the middle proportional to how far out a thing is gives
   * repulsion something to settle against, and a ball with a size of its own.
   */
  gravity: 0.09,
  /**
   * How hard collision insists, 0 to 1.
   *
   * All the way. Every other force here is a preference to be balanced against
   * the others; this one is the rule that two things cannot be in the same
   * place, and a rule obeyed most of the time is not one.
   */
  collideStrength: 1,
  /** Passes per tick. More costs time and settles overlaps that one pass leaves. */
  collideIterations: 2,
} as const;
